import { BCMSRecord, BCMSStateManagerCRUD, Branding, DisabledPrompt, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView, WidgetContainer, WidgetView } from 'components/shared';
import ProfilePicker from "components/shared/widgets/ProfilePicker";
import Classes, { Profile, TaxDocument } from "model";
import React from 'react';
import { SVGAsset } from 'res';
import "./styles.css";
import "./mobile.css";


/**
 * @function TaxDocumentsPage
 * @return {React.Component}
 * @exported true
 * @description The tax documents page. Contains several widgets.
 */
export default function TaxDocumentsPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultData(undefined, context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    onProfilePickerChange: profileID => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "selectedProfileID" ], { id: profileID })
        .submit(setState);
    },

    onProfilePickerSelectClick: evt => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "taxDocuments" ], 
            TaxDocument.initAll().filter(item => item.profile.id === state.data.selectedProfileID.id)
        )
        .submit(setState);
    }
  });

  return (
    <PageContainer className="tax-documents-page" state={{ data, ui }} controller={controller}>
      <WidgetContent />
    </PageContainer>
  );
}


export const getDefaultData = (profileID, context) => {
    const { state } = context ?? {};
    const { branding } = state ?? {};
    const selectedProfileID = profileID ?? Profile.getDefaultBrandedProfile(branding).id;

    return {
        profiles: Profile.initAll(),
        selectedProfileID: { id: selectedProfileID },
        taxDocuments: TaxDocument.initAll().filter(item => item.profile.id === selectedProfileID)
    };
};

export const getDefaultUI = () => ({
  
});

function WidgetContent(props) {
  const { data, onProfilePickerChange, onProfilePickerSelectClick } = useStateManager();

  return (
    <PageContentView>
        <Branding id="multiprofile_abi22" value="true">
            <ProfilePicker title={ `Select Profile for Tax Documents` } profileID={ data.selectedProfileID.id } profiles={ data.profiles }
            ui={{}} onChange={ onProfilePickerChange } onSelectClick={ onProfilePickerSelectClick } disableBusiness="true" />
        </Branding>
        <TaxDocumentsWidget taxDocuments={ data.taxDocuments } />
    </PageContentView>
  );
}

/**
 * @function TaxDocumentsWidget
 * @return {React.Component}
 * @exported false
 * @description A tax documents widget.
 */
function TaxDocumentsWidget(props) {
    const { data, ui } = useStateManager();
    const taxDocuments = data.taxDocuments ?? [];
    const rows = taxDocuments.map((item, key) => <TableRow key={key} data={item} />, []);
    const hasData = rows.length > 0;
    const content = hasData ? rows : <NoDataTableRow />;
    const docsLength = taxDocuments.length;
    const documentsAvailableMessage = `${ docsLength }-${ docsLength } of ${ docsLength } documents`;
    const description = (
        <>
        <span>Description</span>
        <DisabledPrompt>
            <SVGAsset id="SortIcon" className="clickable" />
            </DisabledPrompt>
        </>
    );

    return (
        <WidgetContainer className="tax-documents-widget">
            <WidgetView text="Tax Documents">
                { hasData && 
                <TableView className="tax-documents-table">
                    <TableColumnContainer>
                        <TableColumnView className="text-left date" text={<>Date<DisabledPrompt><SVGAsset id="ArrowUpIcon" className="clickable" /></DisabledPrompt></>} />
                        <TableColumnView className="text-left description" text={ description } />
                        <TableColumnView className="actions-column" />
                    </TableColumnContainer>
                    <TableBody>
                        {content}
                        <tr className="documents-available-message-table-row">
                        <td className="text-center table-item-view" colSpan="3">{ documentsAvailableMessage }</td>
                    </tr>
                    </TableBody>
                    
                </TableView>
                }
                { !hasData && 
                <TableView className="tax-documents-table">
                    <TableBody>
                        {content}
                    </TableBody>
                </TableView>
                }
            </WidgetView>
        </WidgetContainer>
    );
}


/**
 * @function NoFileFormatTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row when no data is available.
 */
function NoDataTableRow(props) {
  return (
    <tr className="no-documents-available-message-table-row">
      <td className="text-center table-item-view" colSpan="3">No documents available</td>
    </tr>
  );
}

/**
 * @function TableRow
 * @return {React.Component}
 * @exported false
 * @description A table row used in the tax documents.
 */
function TableRow(props) {
  const data = props.data;
  const dateLink = (
    <DisabledPrompt>
        <a className="clickable">{ `${data.date.format("Y-m-d")}` }</a>
    </DisabledPrompt>
  );

  return (
    <TableItemContainer>
      <TableItemTextView text={ dateLink } />
      <TableItemTextView text={ data.description } />
      <TableItemView className="actions-column">
        <div className="flex-container flex-horizontal align-center"></div>
      </TableItemView>
    </TableItemContainer>
  );
}
